import { isValid } from 'date-fns'

const SEPARATOR = /[.\-/\s,]/

/**
 * Shape free typing into `DD.MM.YYYY` as the user goes. Digits fill day, month
 * and year in turn and the dots appear on their own, so "01012030" and
 * "1.1.2030" both end up as "01.01.2030". A separator after a single digit
 * pads it, which is what lets "1.1." mean the first of January.
 */
export function maskTypedDate(raw: string): string {
  const parts: string[] = []
  let current = ''

  for (const char of raw) {
    if (/\d/.test(char)) {
      const limit = parts.length < 2 ? 2 : 4
      if (current.length === limit) {
        if (parts.length === 2) continue
        parts.push(current)
        current = ''
      }
      current += char
    } else if (SEPARATOR.test(char) && current && parts.length < 2) {
      parts.push(current.padStart(2, '0'))
      current = ''
    }
  }

  if (!current) return parts.length ? `${parts.join('.')}.` : ''
  return [...parts, current].join('.')
}

/**
 * Read a typed `DD.MM.YYYY` (or `DD.MM.YY`, taken as 20YY) into a date at noon,
 * the time single dates are stored at to keep them clear of timezone shifts.
 * Anything that is not a real calendar day, like 31.02, is `null`.
 */
export function parseTypedDate(text: string): Date | null {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(text.trim())
  const [, dayText, monthText, yearText] = match ?? []
  if (!dayText || !monthText || !yearText) return null

  const day = Number(dayText)
  const month = Number(monthText)
  const year = Number(yearText) + (yearText.length === 2 ? 2000 : 0)

  const date = new Date(year, month - 1, day, 12, 0)
  if (
    !isValid(date) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return date
}

/** The text a date is shown as in the typed field. */
export function formatTypedDate(date: Date | null): string {
  if (!date) return ''

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${date.getFullYear()}`
}
